import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stack,
  Alert,
  Chip,
  CircularProgress,
  Divider,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  AccessTime as TimeIcon,
} from "@mui/icons-material";
import type { HoldList } from "api-client/models";
import { formatDateLong } from "@/utils/dateUtils";

interface ChallengeResponseDialogProps {
  open: boolean;
  onClose: () => void;
  hold: HoldList;
  onConfirm: (holdId: string) => Promise<void>;
  onRelease: (holdId: string) => Promise<void>;
}

/**
 * Dialog for responding to challenges on holds.
 * Challenged party can:
 * - Confirm: Keep their hold (reject challenge)
 * - Release: Give up hold (challenge cascades)
 */
export const ChallengeResponseDialog: React.FC<
  ChallengeResponseDialogProps
> = ({ open, onClose, hold, onConfirm, onRelease }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionType, setActionType] = useState<"confirm" | "release" | null>(
    null,
  );

  const getPositionText = (position: number): string => {
    switch (position) {
      case 1:
        return "1st";
      case 2:
        return "2nd";
      case 3:
        return "3rd";
      case 4:
        return "4th";
      default:
        return `${position}th`;
    }
  };

  const getTimeRemaining = (): string => {
    if (!hold.challengeResponseDeadline) return "Unknown";

    const deadline = new Date(hold.challengeResponseDeadline);
    const now = new Date();
    const diff = deadline.getTime() - now.getTime();

    if (diff <= 0) return "Expired";

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
      return `${hours}h ${minutes}m remaining`;
    }
    return `${minutes} minutes remaining`;
  };

  const handleConfirm = async () => {
    if (loading) return;
    setActionType("confirm");
    setLoading(true);
    setError(null);

    try {
      await onConfirm(hold.id);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to confirm hold");
    } finally {
      setLoading(false);
      setActionType(null);
    }
  };

  const handleRelease = async () => {
    if (loading) return;
    setActionType("release");
    setLoading(true);
    setError(null);

    try {
      await onRelease(hold.id);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to release hold");
    } finally {
      setLoading(false);
      setActionType(null);
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} disableEscapeKeyDown={loading} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <WarningIcon color="warning" />
          <Typography variant="h6">Challenge Response Required</Typography>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Challenge Alert */}
          <Alert severity="warning" icon={<WarningIcon />}>
            <Typography variant="body2" fontWeight="bold">
              Your hold is being challenged!
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              You must respond within 24 hours or your hold will be
              automatically released.
            </Typography>
          </Alert>

          {/* Hold Information */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Hold Details
            </Typography>
            <Stack spacing={1}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">
                  Date:
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {formatDateLong(hold.holdDate)}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">
                  Stage:
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {hold.stageName} at {hold.stageVenueName}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">
                  Your Position:
                </Typography>
                <Chip
                  label={`${getPositionText(hold.holdPosition || 0)} position`}
                  size="small"
                  color="primary"
                />
              </Box>
              {hold.challengerTeamName && (
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" color="text.secondary">
                    Challenged By:
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {hold.challengerTeamName}
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">
                  <TimeIcon
                    sx={{ fontSize: 16, verticalAlign: "text-bottom", mr: 0.5 }}
                  />
                  Time Remaining:
                </Typography>
                <Chip
                  label={getTimeRemaining()}
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              </Box>
            </Stack>
          </Box>

          <Divider />

          {/* Response Options */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Your Response Options
            </Typography>

            {/* Confirm Option */}
            <Box
              sx={{
                p: 2,
                mb: 2,
                border: "2px solid",
                borderColor: "statusText.ready",
                borderRadius: 1,
                bgcolor: "success.light",
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <CheckCircleIcon color="success" />
                <Typography variant="subtitle2" fontWeight="bold">
                  Confirm - Keep Your Hold
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                • You keep your hold in{" "}
                {getPositionText(hold.holdPosition || 0)} position
                <br />
                • Your hold becomes confirmed (cannot be challenged again)
                <br />
                • The challenger loses this date
                <br />• All lower holds on this date are released
              </Typography>
            </Box>

            {/* Release Option */}
            <Box
              sx={{
                p: 2,
                border: "2px solid",
                borderColor: "statusText.attention",
                borderRadius: 1,
                bgcolor: "error.light",
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <WarningIcon color="error" />
                <Typography variant="subtitle2" fontWeight="bold">
                  Release - Give Up Hold
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                • You permanently lose this hold
                <br />
                • Challenge cascades to next position in top-down order
                <br />
                • Remaining holds are reordered
                <br />• This action cannot be undone
              </Typography>
            </Box>
          </Box>

          {/* Warning */}
          <Alert severity="info" icon={<TimeIcon />}>
            <Typography variant="body2">
              <strong>Important:</strong> If you don't respond within the
              deadline, your hold will be automatically released and the
              challenge will cascade.
            </Typography>
          </Alert>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={loading} autoFocus>
          Close
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={handleRelease}
          variant="outlined"
          color="error"
          disabled={loading}
          startIcon={
            loading && actionType === "release" ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <WarningIcon />
            )
          }
        >
          {loading && actionType === "release"
            ? "Releasing..."
            : "Release Hold"}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="success"
          disabled={loading}
          startIcon={
            loading && actionType === "confirm" ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <CheckCircleIcon />
            )
          }
        >
          {loading && actionType === "confirm"
            ? "Confirming..."
            : "Confirm Hold"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
